/*
 *
 * TransactionReversePopup
 *
 * @version 1.0.0
 * @author Ivana Piscitelli
 * @since 09-Jun-2025
 */

import React, { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import { AnalyticsContext } from 'Hoc/AnalyticsProvider'
import MerchantModal from '@nexidigital/merchantmodal'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import formattedMessage from './languageModule'
import DatePicker from '@nexidigital/datepicker'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import PortalRadioBox from '@nexidigital/portalradiobox'
import PortalTextInput from '@nexidigital/portaltextinput'
import './TransactionReversePopup.scss'
import { getDatePickerLocale } from 'Utility/globalMethods'

const TransactionReversePopup = (props) => {
  const intl = useIntl()
  const analyticsContext = useContext(AnalyticsContext)
  const { setFormInteraction } = analyticsContext || {}

  // Keys for form fields
  const FORM_KEYS = {
    START_DATE: 'startDate',
    END_DATE: 'endDate',
    CARD_NUMBER: 'cardNumber',
    AUTH_CODE: 'authCode',
    SELECTED_OPTION: 'selectedOption'
  }

  // Default range: last 7 days (from 6 days ago to today)
  const defaultRange = {
    start: moment().subtract(6, 'days').format('DD/MM/YYYY'),
    end: moment().format('DD/MM/YYYY')
  }

  const validationSchema = Yup.object().shape({
    [FORM_KEYS.SELECTED_OPTION]: Yup.string().required().oneOf(['cardNumber', 'authCode']),
    [FORM_KEYS.START_DATE]: Yup.string()
      .required(formattedMessage.customDateRequiredFieldError)
      .test(
        'date-format',
        formattedMessage.customDateFormatFieldError,
        (value) => !value || /^\d{2}\/\d{2}\/\d{4}$/.test(value)
      )
      .test(
        'valid-format',
        formattedMessage.customDateInvalidError,
        (value) => !!value && moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).isValid()
      )
      .test(
        'min-12-months-strict',
        formattedMessage.dateLimitError(),
        (value) =>
          !!value &&
          // Start date must be strictly within the last 12 months (not exactly 12 months ago)
          moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).isAfter(moment().subtract(12, 'months'), 'day')
      )
      .test('start-before-end', formattedMessage.customDateRangeEndDatePriorToTheStartDateReverse, function (value) {
        const { endDate } = this.parent
        if (value && endDate && moment(endDate, 'DD/MM/YYYY', true).isValid()) {
          return moment(value, 'DD/MM/YYYY').isSameOrBefore(moment(endDate, 'DD/MM/YYYY'))
        }
        return true
      }),
    [FORM_KEYS.END_DATE]: Yup.string()
      .required(formattedMessage.customDateRequiredFieldError)
      .test(
        'date-format',
        formattedMessage.customDateFormatFieldError,
        (value) => !value || /^\d{2}\/\d{2}\/\d{4}$/.test(value)
      )
      .test(
        'valid-format',
        formattedMessage.customDateInvalidError,
        (value) => !!value && moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).isValid()
      )
      .test(
        'not-after-today',
        formattedMessage.customDateEndDateAfterTodayError,
        (value) => !!value && moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true).isSameOrBefore(moment(), 'day')
      )
      .test('max-12-months-range-strict', formattedMessage.customDateStartDateTooOldError, function (value) {
        const { startDate } = this.parent
        if (value && startDate && moment(startDate, 'DD/MM/YYYY', true).isValid()) {
          // Range must be strictly less than 12 months
          return moment(value, 'DD/MM/YYYY').diff(moment(startDate, 'DD/MM/YYYY'), 'months', true) < 12
        }
        return true
      })
      .test('end-after-start', formattedMessage.customDateRangeEndDatePriorToTheStartDate, function (value) {
        const { startDate } = this.parent
        if (value && startDate && moment(startDate, 'DD/MM/YYYY', true).isValid()) {
          return moment(value, 'DD/MM/YYYY').isSameOrAfter(moment(startDate, 'DD/MM/YYYY'))
        }
        return true
      }),
    [FORM_KEYS.CARD_NUMBER]: Yup.string().when([FORM_KEYS.SELECTED_OPTION], {
      is: 'cardNumber',
      then: (schema) =>
        schema
          .required(formattedMessage.customDateRequiredFieldError)
          .test('pan-format', formattedMessage.customPanFormatFieldError, (value) => !value || /^\d{4}$/.test(value)),
      otherwise: (schema) => schema.notRequired()
    }),
    [FORM_KEYS.AUTH_CODE]: Yup.string().when([FORM_KEYS.SELECTED_OPTION], {
      is: 'authCode',
      then: (schema) =>
        schema
          .required(formattedMessage.customDateRequiredFieldError)
          .test(
            'auth-code-format',
            formattedMessage.customAuthCodeFormatFieldError,
            (value) => !value || /^[A-Za-z0-9]{5,6}$/.test(value)
          ),
      otherwise: (schema) => schema.notRequired()
    })
  })

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      [FORM_KEYS.START_DATE]: defaultRange.start,
      [FORM_KEYS.END_DATE]: defaultRange.end,
      [FORM_KEYS.CARD_NUMBER]: '',
      [FORM_KEYS.AUTH_CODE]: '',
      [FORM_KEYS.SELECTED_OPTION]: 'cardNumber'
    },
    validationSchema,
    onSubmit: (values) => {
      const { selectedOption, startDate, endDate, cardNumber, authCode } = values
      const requestParams = {
        refoundTransactionsSearchParams: {
          startDate: moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          endDate: moment(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          filterType: {
            transactionData: {
              search: {
                type:
                  selectedOption && selectedOption === 'cardNumber'
                    ? 'PAN'
                    : selectedOption === 'authCode'
                      ? 'AUTH_CODE'
                      : null,
                value: cardNumber || authCode || null
              }
            }
          },
          showAnalytics: true
        }
      }
      props.actions.doCheckTransactionsTab(requestParams || {})
    }
  })

  useEffect(() => {
    // If the are un endDate errors and the fields are not touched, set them as touched
    // For example max-12-months-range must be show only in endDate field
    if (formik.errors.endDate && !formik.touched.endDate) {
      formik.setFieldTouched(FORM_KEYS.END_DATE, true)
    }
  }, [formik.errors])

  // Effect to set initial values on mount
  useEffect(() => {
    // Set false for the third parameter to avoid validation on initial set
    formik.setFieldValue(FORM_KEYS.START_DATE, defaultRange.start, false)
    formik.setFieldValue(FORM_KEYS.END_DATE, defaultRange.end, false)
    formik.setFieldValue(FORM_KEYS.SELECTED_OPTION, 'cardNumber', false)

    // Set touched for both date fields to true to show empty errors in case of user deletes manually
    formik.setTouched(
      {
        [FORM_KEYS.START_DATE]: true,
        [FORM_KEYS.END_DATE]: true,
        [FORM_KEYS.CARD_NUMBER]: false,
        [FORM_KEYS.AUTH_CODE]: false,
        [FORM_KEYS.SELECTED_OPTION]: false
      },
      false
    )

    // Fix async validation issue
    // Workaround to ensure validation runs after setting values
    setTimeout(() => {
      formik.validateForm()
    }, 0)

    // Analytics: impression when the modal opens
    if (setFormInteraction) {
      setFormInteraction('impression', 'TransactionReversePopup', '', 1, '')
    }
  }, [])

  // Handle date change for start and end dates
  const handleDateChange = (date, field) => {
    formik.setFieldValue(field, date)
    if (setFormInteraction) {
      setFormInteraction(date, 'TransactionReversePopup', field, formik.errors[field] ? 0 : 1, formik.errors[field] || '')
    }
  }

  // Handler for the change of the radio option
  const handleOptionChange = (value) => {
    formik.setFieldValue(FORM_KEYS.SELECTED_OPTION, value)
    // Reset fields when changing option
    formik.setFieldValue(FORM_KEYS.CARD_NUMBER, '')
    formik.setFieldValue(FORM_KEYS.AUTH_CODE, '')
    formik.setFieldTouched(FORM_KEYS.CARD_NUMBER, false)
    formik.setFieldTouched(FORM_KEYS.AUTH_CODE, false)
    // Reset errors
    formik.setFieldError(FORM_KEYS.CARD_NUMBER, undefined)
    formik.setFieldError(FORM_KEYS.AUTH_CODE, undefined)
    if (setFormInteraction) {
      setFormInteraction(value, 'TransactionReversePopup', FORM_KEYS.SELECTED_OPTION, 1, '')
    }
  }

  // Handler for the card number: allow only digits (no letters), no errors while typing
  const handleCardNumberChange = (e) => {
    const value = e.target.value
    // Disallow non-digits entirely
    if (!/^\d*$/.test(value)) {
      return
    }
    if (value.length <= 4) {
      formik.setFieldValue(FORM_KEYS.CARD_NUMBER, value)
      formik.setFieldTouched(FORM_KEYS.CARD_NUMBER, false, false)
      formik.setFieldError(FORM_KEYS.CARD_NUMBER, undefined)
    }
    if (setFormInteraction) {
      setFormInteraction(value, 'TransactionReversePopup', FORM_KEYS.CARD_NUMBER, formik.errors[FORM_KEYS.CARD_NUMBER] ? 0 : 1, formik.errors[FORM_KEYS.CARD_NUMBER] || '')
    }
  }

  // Handler for the blur of the card number
  const handleCardNumberBlur = () => {
    const value = formik.values[FORM_KEYS.CARD_NUMBER] || ''
    // Mark as touched
    formik.setFieldTouched(FORM_KEYS.CARD_NUMBER, true, false)
    if (value.length === 0) {
      // Defer to Yup required validation
      formik.validateField(FORM_KEYS.CARD_NUMBER)
    } else if (!/^\d{4}$/.test(value)) {
      // Keep current content and show 4-digits error
      formik.setFieldError(FORM_KEYS.CARD_NUMBER, formattedMessage.customPanFormatFieldError)
    } else {
      // Valid: clear any previous errors
      formik.setFieldError(FORM_KEYS.CARD_NUMBER, undefined)
    }
    if (setFormInteraction) {
      setFormInteraction('focus', 'TransactionReversePopup', FORM_KEYS.CARD_NUMBER, formik.errors[FORM_KEYS.CARD_NUMBER] ? 0 : 1, formik.errors[FORM_KEYS.CARD_NUMBER] || '')
    }
  }

  // Keydown guard: block letters and prevent typing a 5th digit
  const handleCardNumberKeyDown = (e) => {
    const isDigit = /^\d$/.test(e.key)
    const currentLen = (formik.values[FORM_KEYS.CARD_NUMBER] || '').length

    // Only prevent typing more than 4 digits
    if (isDigit && currentLen >= 4) {
      e.preventDefault()
      // Show 4-digits error only when attempting to exceed max length
      formik.setFieldTouched(FORM_KEYS.CARD_NUMBER, true, false)
      formik.setFieldError(FORM_KEYS.CARD_NUMBER, formattedMessage.customPanFormatFieldError)
    }
  }

  // Handler for the authorization code (only alphanumeric)
  const handleAuthCodeChange = (e) => {
    const value = e.target.value

    // Allow only alphanumeric characters and max 6 characters
    if (/^[A-Za-z0-9]*$/.test(value) && value.length <= 6) {
      formik.setFieldValue(FORM_KEYS.AUTH_CODE, value)
    }
    if (setFormInteraction) {
      setFormInteraction(value, 'TransactionReversePopup', FORM_KEYS.AUTH_CODE, formik.errors[FORM_KEYS.AUTH_CODE] ? 0 : 1, formik.errors[FORM_KEYS.AUTH_CODE] || '')
    }
  }

  // Handler for the blur of the authorization code
  const handleAuthCodeBlur = () => {
    formik.setFieldTouched(FORM_KEYS.AUTH_CODE, true)
    formik.validateField(FORM_KEYS.AUTH_CODE)
    if (setFormInteraction) {
      setFormInteraction('focus', 'TransactionReversePopup', FORM_KEYS.AUTH_CODE, formik.errors[FORM_KEYS.AUTH_CODE] ? 0 : 1, formik.errors[FORM_KEYS.AUTH_CODE] || '')
    }
  }

  // Reset PortalTextInput input on click of the icon X
  const handleSearchInputReset = (formKey) => {
    formik.setFieldValue(formKey, '')
    formik.setFieldTouched(formKey, false)
  }

  // Helpers to keep primaryAction concise
  const mergeErrors = (prev, next) => {
    const merged = { ...next }
    Object.keys(prev || {}).forEach((key) => {
      if (prev[key]) {
        merged[key] = prev[key]
      }
    })
    return merged
  }

  const touchFieldsWithErrors = (errors) => {
    const touchedMap = {}
    Object.keys(errors || {}).forEach((key) => {
      touchedMap[key] = true
    })
    if (Object.keys(touchedMap).length) {
      formik.setTouched(touchedMap, true)
    }
  }

  const handlePrimaryAction = async () => {
    // Validate and block submit if errors, preserving existing messages
    const prevErrors = { ...formik.errors }
    const newErrors = await formik.validateForm()
    const hasErrors = newErrors && Object.keys(newErrors).length > 0
    if (hasErrors) {
      const merged = mergeErrors(prevErrors, newErrors)
      formik.setErrors(merged)
      touchFieldsWithErrors(merged)
      if (setFormInteraction) {
        Object.keys(merged).forEach((fieldKey) => {
          const fieldError = merged[fieldKey]
          const errorMessage = fieldError?.props?.defaultMessage || fieldError || ''
          setFormInteraction('error-field-value', 'TransactionReversePopup', fieldKey, 0, errorMessage)
        })
      }
      return
    }
    formik.handleSubmit()
  }
  return (
    <Toggle>
      <MerchantModal
        onClose={() => props.actions.doHidePopup()}
        primaryAction={handlePrimaryAction}
        primaryButtonLabel={formattedMessage.primaryButtonLabel}
        primaryButtonLoading={props.isLoading}
        closeLabel={formattedMessage.close}
        topBarTitle={formattedMessage.title}
        customAnalyticsDescription={formattedMessage.cardSubtitle}
      >
        <div className='transactionReverseContent'>
          <MerchantGenericCard title={formattedMessage.cardTitle} subtitle={formattedMessage.cardSubtitle}>
            <DatePicker
              name='dateRange'
              name1={FORM_KEYS.START_DATE}
              name2={FORM_KEYS.END_DATE}
              label1={formattedMessage.dataRangeStartLabel}
              label2={formattedMessage.dataRangeEndLabel}
              isRange
              smartYearNavigation
              // Strict window: not earlier than exactly 12 months ago
              minDate={moment().subtract(12, 'months').add(1, 'day')}
              maxDate={moment()}
              value1={formik.values[FORM_KEYS.START_DATE]}
              value2={formik.values[FORM_KEYS.END_DATE]}
              placeholder1={intl.formatMessage({
                id: 't.app.transactions.filtersModal.dataRangeStartDatePlaceholder',
                defaultMessage: 'Inserisci data inizio'
              })}
              placeholder2={intl.formatMessage({
                id: 't.app.transactions.filtersModal.dataRangeEndDatePlaceholder',
                defaultMessage: 'Inserisci data fine'
              })}
              onChange1={(date) => handleDateChange(date, FORM_KEYS.START_DATE)}
              onChange2={(date) => handleDateChange(date, FORM_KEYS.END_DATE)}
              error1={formik.touched[FORM_KEYS.START_DATE] && formik.errors[FORM_KEYS.START_DATE]}
              errorMessage1={formik.errors[FORM_KEYS.START_DATE]}
              error2={formik.touched[FORM_KEYS.END_DATE] && formik.errors[FORM_KEYS.END_DATE]}
              errorMessage2={formik.errors[FORM_KEYS.END_DATE]}
              onBlur1={() => formik.setFieldTouched(FORM_KEYS.START_DATE, true)}
              onBlur2={() => formik.setFieldTouched(FORM_KEYS.END_DATE, true)}
              labelYear={intl.formatMessage({ id: 't.app.year' })}
              labelMonth={intl.formatMessage({ id: 't.app.month' })}
              locale={getDatePickerLocale(intl.locale)}
              labelsLocale={intl.locale}
            />
            <div className='transactionReverseRadioBox'>
              <PortalRadioBox
                centered
                cleanField
                defaultValue='cardNumber'
                value={formik.values[FORM_KEYS.SELECTED_OPTION]}
                onChange={handleOptionChange}
                options={[
                  {
                    label: formattedMessage.cardNumberLabel,
                    value: 'cardNumber',
                    children: formik.values[FORM_KEYS.SELECTED_OPTION] === 'cardNumber' && (
                      <PortalTextInput
                        cleanField
                        id='cardNumber'
                        name={FORM_KEYS.CARD_NUMBER}
                        label={formattedMessage.placeholderPan}
                        placeholder={intl.formatMessage({ id: 't.app.transactions.reversePopup.placeholderPan' })}
                        analyticsLabel={formattedMessage.cardNumberLabel}
                        type='tel'
                        inputMode='numeric'
                        preventPaste
                        value={formik.values[FORM_KEYS.CARD_NUMBER]}
                        handleCleanIconClick={() => handleSearchInputReset(FORM_KEYS.CARD_NUMBER)}
                        onChange={handleCardNumberChange}
                        onKeyDown={handleCardNumberKeyDown}
                        onBlur={handleCardNumberBlur}
                        error={formik.touched[FORM_KEYS.CARD_NUMBER] && Boolean(formik.errors[FORM_KEYS.CARD_NUMBER])}
                        errorText={formik.touched[FORM_KEYS.CARD_NUMBER] && formik.errors[FORM_KEYS.CARD_NUMBER]}
                        maxLength={4}
                      />
                    )
                  },
                  {
                    label: formattedMessage.authorizationCodeLabel,
                    value: 'authCode',
                    children: formik.values[FORM_KEYS.SELECTED_OPTION] === 'authCode' && (
                      <PortalTextInput
                        cleanField
                        id='authCode'
                        name={FORM_KEYS.AUTH_CODE}
                        label={formattedMessage.authorizationCodeLabel}
                        placeholder={intl.formatMessage({
                          id: 't.app.transactions.reversePopup.placeholderAuthorizationCode'
                        })}
                        analyticsLabel={formattedMessage.authorizationCodeLabel}
                        type='text'
                        value={formik.values[FORM_KEYS.AUTH_CODE]}
                        handleCleanIconClick={() => handleSearchInputReset(FORM_KEYS.AUTH_CODE)}
                        onChange={handleAuthCodeChange}
                        onBlur={handleAuthCodeBlur}
                        error={formik.touched[FORM_KEYS.AUTH_CODE] && Boolean(formik.errors[FORM_KEYS.AUTH_CODE])}
                        errorText={formik.touched[FORM_KEYS.AUTH_CODE] && formik.errors[FORM_KEYS.AUTH_CODE]}
                        maxLength={6}
                        helperText={formattedMessage.helperTextAuthCode}
                      />
                    )
                  }
                ]}
              />
            </div>
          </MerchantGenericCard>
        </div>
      </MerchantModal>
    </Toggle>
  )
}

TransactionReversePopup.propTypes = {
  actions: PropTypes.object,
  isLoading: PropTypes.bool
}

TransactionReversePopup.defaultProps = {
  actions: {},
  isLoading: false
}

export default TransactionReversePopup
